// The chat's hands on the canvas of traces that is open: tools a chat's run
// carries while a workspace is open as it starts (ChatConsole's
// withWorkspaceTools), acting on that canvas through its store, so they need
// no page to be mounted and the canvas draws whatever they leave there:
//   workspace_state     what is on the canvas: its traces, their lanes and
//                       turn counts, the notes, readings and claims on them,
//                       and what is about them that can be opened;
//   workspace_lay_on    lay sessions on the canvas;
//   workspace_take_off  take sessions off it (never its anchor);
//   workspace_open      open what is about its traces (its card under its
//                       trace), or close it;
//   workspace_point     pin a note on a turn (and a lane) of a trace, laying
//                       the trace on first if it is not there.
// A run's tools act on the workspace open as it started (workspaceToolsWhile):
// the reader going to another meanwhile does not take the run's work there,
// and a run whose workspace is no longer kept gets an error rather than
// writing to another. A note is kept by the trace it annotates
// (lib/traceNoteStore.js), and shows wherever that trace is read; one chat's
// tools are told which chat they are (`chat`: its graph key and title), so
// its notes say where they came from.
import { Type } from "@earendil-works/pi-ai";
import { askWorkspaceOpen, requestWorkspaceFocus } from "./workspaceStore.js";
import { canvasThings, canvasTraces, layOnTraceCanvas, takeOffTraceCanvas, traceCanvasKept, traceCanvasOpen, traceCanvasSnapshot } from "./traceCanvasStore.js";
import { WORKSPACE_POINT } from "./chatWorkspaceNotes.js";
import { addTraceNotes, traceNotesOf } from "./traceNoteStore.js";

const SESSION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_LAY_ON = 12;
// How many of what is about the workspace's traces its state lists, and one
// call opens or closes.
const MAX_THINGS = 150;
const MAX_OPEN = 40;

// The workspace a tool acts on (its run's, else the one open), or the error
// it answers with when there is none.
function requireCanvas(home) {
  const id = (home ? home() : traceCanvasOpen()?.id) || null;
  if (!id || !traceCanvasKept(id)) throw new Error("No investigation is open. Ask the reader to open a trace, or this chat's case, as an investigation first.");
  return { id, title: traceCanvasSnapshot(id).title || "" };
}

function sessionId(raw, what = "session_id") {
  const id = String(raw || "").trim().toLowerCase();
  if (!SESSION_ID.test(id)) throw new Error(`${what} must be a trace id (a UUID), got "${String(raw || "")}".`);
  return id;
}

const result = (text, details) => ({ content: [{ type: "text", text }], details });
const count = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// `home`: the workspace the tools act on (its id), in place of the one open.
export function createWorkspaceChatTools({ chat = null, home = null } = {}) {
  return [
    {
      name: "workspace_state",
      label: "Read the investigation",
      description: "Read what is on the investigation of traces the reader has open: its traces (session ids, titles, which one anchors it if any, how many turns each has and the number its oldest one prints as, the lanes each shows, and for one inside an investigation on the board, which), the notes, readings and claims pinned on their cells, and `things`: what is about each trace on the board (findings, notes, the chat's claims, sub-agents), each with the id workspace_open takes and whether its card is open. Call it before pointing at cells, to name turns and lanes the investigation has, and before opening things.",
      parameters: Type.Object({}),
      async execute() {
        const open = requireCanvas(home);
        const c = traceCanvasSnapshot(open.id);
        // A long trace's workspace holds its newest turns: they print as
        // their place in the whole record, from first_turn on.
        const traces = canvasTraces(open.id).map((t) => ({
          session_id: t.id,
          title: t.title || null,
          anchor: t.anchor,
          inside: t.inside || null,
          status: t.card?.status || (t.inside ? "inside an investigation on the board" : "not read yet"),
          turns: t.card?.turns ?? null,
          first_turn: t.card?.firstTurn || 0,
          lanes: t.card?.lanes || [],
        }));
        // Notes and readings are kept by their turns' places in the record:
        // every note on the traces the workspace holds, the ones this chat
        // pinned marked as its own.
        const me = chat?.()?.key ?? null;
        const notes = traces.flatMap((t) => traceNotesOf(t.session_id)).map((n) => ({
          session_id: n.session, turn: n.printed ?? null, message_id: n.messageId || null, lane: n.lane || null, note: n.text, yours: me != null && n.chat?.key === me,
        }));
        const readings = (c.readings || []).map((r) => ({ session_id: r.session, turn: r.turn, lane: r.lane }));
        // What its board has about the traces, while it is the board in
        // front: as many as a reply should carry, the rest counted.
        const all = canvasThings(open.id);
        const things = (all || []).slice(0, MAX_THINGS).map((t) => ({
          id: t.id, kind: t.kind, session_id: t.session, turn: t.turn ?? null, lane: t.lane || null, label: t.label || null, open: !!t.open,
        }));
        const state = { workspace: open.title || null, traces, notes, readings, things: all ? things : null };
        if (all && all.length > things.length) state.things_more = all.length - things.length;
        return result(JSON.stringify(state), { kind: "workspaceState", traces: traces.length, notes: notes.length, things: all ? all.length : null });
      },
    },
    {
      name: "workspace_lay_on",
      label: "Lay traces on the investigation",
      description: "Lay sessions on the investigation of traces the reader has open, beside the trace it is anchored on, where they read side by side on one clock, wired wherever they share content. Takes the session ids of conversations (their trace_id / session_id).",
      parameters: Type.Object({
        session_ids: Type.Array(Type.String(), { minItems: 1, maxItems: MAX_LAY_ON, description: "Session ids (UUIDs) to lay on the investigation" }),
      }),
      async execute(_toolCallId, args) {
        const open = requireCanvas(home);
        const ids = [...new Set((args?.session_ids || []).map((id) => sessionId(id)))];
        if (!ids.length) throw new Error("Give at least one trace id.");
        const before = new Set(canvasTraces(open.id).map((t) => t.id));
        layOnTraceCanvas(open.id, ids);
        requestWorkspaceFocus(ids[ids.length - 1]);
        const laid = ids.filter((id) => !before.has(id));
        const already = ids.filter((id) => before.has(id));
        return result(
          `Laid ${laid.length} trace${laid.length === 1 ? "" : "s"} on the investigation${already.length ? `; ${already.length} ${already.length === 1 ? "was" : "were"} on it already` : ""}. It now holds ${canvasTraces(open.id).length}.`,
          { kind: "workspaceLayOn", laid, already },
        );
      },
    },
    {
      name: "workspace_take_off",
      label: "Take traces off the investigation",
      description: "Take sessions off the investigation of traces the reader has open: their cards go, with the place, size and readings the reader gave them there; the notes pinned on them stay with their traces, and workspace_lay_on lays one on again. The trace the investigation is anchored on stays, as do sessions only inside another investigation on the board. Takes session ids as workspace_state lists them.",
      parameters: Type.Object({
        session_ids: Type.Array(Type.String(), { minItems: 1, maxItems: MAX_LAY_ON, description: "Session ids (UUIDs) to take off the investigation" }),
      }),
      async execute(_toolCallId, args) {
        const open = requireCanvas(home);
        const ids = [...new Set((args?.session_ids || []).map((id) => sessionId(id)))];
        if (!ids.length) throw new Error("Give at least one trace id.");
        const c = traceCanvasSnapshot(open.id);
        const laid = new Set(c.layered.map((t) => String(t.id).toLowerCase()));
        const inside = new Set(canvasTraces(open.id).filter((t) => t.inside).map((t) => t.id));
        const anchor = String(c.anchor || "").toLowerCase();
        const taken = [];
        const kept = [];
        const notOn = [];
        for (const id of ids) {
          if (id === anchor || inside.has(id)) kept.push(id);
          else if (!laid.has(id)) notOn.push(id);
          else {
            takeOffTraceCanvas(open.id, id);
            taken.push(id);
          }
        }
        const said = [`Took ${count(taken.length, "trace")} off the investigation`];
        if (kept.length) said.push(`${count(kept.length, "stays", "stay")} (${kept.map((id) => (id === anchor ? `${id} anchors it` : `${id} is inside another investigation on it`)).join("; ")})`);
        if (notOn.length) said.push(`${count(notOn.length, "was", "were")} not on it`);
        return result(`${said.join("; ")}. It now holds ${canvasTraces(open.id).length}.`, { kind: "workspaceTakeOff", taken, kept, notOn });
      },
    },
    {
      name: "workspace_open",
      label: "Open on the investigation",
      description: "Open what is about the traces on the investigation the reader has open (findings, notes, the chat's claims, sub-agents): its card shows under its trace at its moment, as when the reader opens it. Close them with open: false. Nothing shows under a trace until it is opened (the reader opens one from its trace's tag, its mark on its cell, or its pin), so open only what the reader should read now. Takes the ids workspace_state lists under `things`.",
      parameters: Type.Object({
        ids: Type.Array(Type.String(), { minItems: 1, maxItems: MAX_OPEN, description: "Ids from workspace_state's `things`" }),
        open: Type.Optional(Type.Boolean({ description: "true (the default) opens them; false closes them" })),
      }),
      async execute(_toolCallId, args) {
        const ws = requireCanvas(home);
        const all = canvasThings(ws.id);
        if (!all) throw new Error("The investigation's board is not showing, so what is on it cannot be named. Ask the reader to bring it up.");
        const opening = args?.open !== false;
        const ids = [...new Set((args?.ids || []).map((id) => String(id || "").trim()).filter(Boolean))];
        if (!ids.length) throw new Error("Give at least one id from workspace_state's `things`.");
        const byId = new Map(all.map((t) => [t.id, t]));
        const unknown = ids.filter((id) => !byId.has(id));
        const changed = ids.filter((id) => byId.has(id) && !!byId.get(id).open !== opening);
        if (changed.length) askWorkspaceOpen(changed, opening);
        const same = ids.length - unknown.length - changed.length;
        const said = [`${opening ? "Opened" : "Closed"} ${changed.length} of ${count(ids.length, "thing")}`];
        if (same) said.push(`${same} ${opening ? "already open" : "not open"}`);
        if (unknown.length) said.push(`not on the investigation: ${unknown.slice(0, 6).join(", ")}${unknown.length > 6 ? ", ..." : ""}`);
        return result(`${said.join("; ")}.`, { kind: "workspaceOpen", open: opening, changed, unknown });
      },
    },
    createPointTool({ chat, home }),
  ];
}

// The pin: a note on a cell of a trace, kept by the trace (lib/traceNoteStore.js).
// On the workspace open (`home`), it lays the trace on first if it is not
// there. `loose`: with no workspace open, a pin on a session the chat reads,
// shown wherever that trace is opened.
export function createPointTool({ chat = null, home = null, loose = false } = {}) {
  return {
    name: WORKSPACE_POINT,
    label: "Point at a cell",
    description: loose
      ? "Pin a note on a message of a session you read, to show the reader where the evidence for what you say is: it shows on that trace's card and lanes wherever it is opened. Name the message by its message_id as [brackets] show it in what you read, and say briefly what to see there."
      : "Pin a note on a cell of a trace on the investigation the reader has open, to show them where the evidence for what you say is. Name the turn by its message_id (from the conversation), or by its turn number as the investigation prints it (T<n>, its place in the whole record: a trace's turns run from its first_turn in workspace_state), and optionally the lane (a label or id from workspace_state; the conversation's lane otherwise). The note says, briefly, what to see there. Lays the trace on first if it is not on the investigation.",
    parameters: Type.Object({
      session_id: Type.String({ description: "The session id (UUID) of the trace" }),
      message_id: Type.Optional(Type.String({ description: "The message id of the turn" })),
      ...(loose ? {} : { turn: Type.Optional(Type.Integer({ minimum: 0, description: "The turn as the investigation prints it, T<n>" })) }),
      lane: Type.Optional(Type.String({ description: "A lane label or id" })),
      note: Type.String({ minLength: 1, maxLength: 400, description: "What the reader should see at this cell" }),
    }),
    // The note is kept by its trace, and this call's result says where it
    // is and what it says, so the chat's transcript can put it back
    // (lib/chatWorkspaceNotes.js).
    async execute(toolCallId, args) {
      const open = loose ? null : requireCanvas(home);
      const session = sessionId(args?.session_id);
      const messageId = args?.message_id ? String(args.message_id).trim().replace(/^\[|\]$/g, "") : null;
      const turn = !loose && Number.isInteger(args?.turn) ? args.turn : null;
      if (messageId == null && turn == null) throw new Error(loose ? "Name the message: give message_id." : "Name the turn: give message_id or turn.");
      const text = String(args?.note || "").trim();
      if (!text) throw new Error("Say what the reader should see there in `note`.");
      if (open) layOnTraceCanvas(open.id, [session]);
      addTraceNotes([{ id: `note:${toolCallId}`, session, messageId, printed: turn, lane: args?.lane ? String(args.lane) : null, text, chat: chat?.() || null, at: Date.now() }]);
      if (open) requestWorkspaceFocus(session);
      const where = messageId ? `message ${messageId}` : `T${turn}`;
      return result(`Pinned the note on ${session} at ${where}${args?.lane ? ` (${args.lane})` : ""}. ${open ? "It shows under that trace's card on the investigation." : "It shows on that trace wherever it is opened."}`, { kind: "workspacePoint", session, messageId, turn, lane: args?.lane || null, note: text });
    },
  };
}

// What a chat's run carries: the workspace's tools while one is open as the
// run starts (`when` is asked then), acting on that one for the whole run.
export function workspaceToolsWhile({ chat = null } = {}) {
  let home = null;
  const tools = createWorkspaceChatTools({ chat, home: () => home });
  const when = () => {
    home = traceCanvasOpen()?.id || null;
    return home != null;
  };
  return { tools, when };
}

// What a chat beside a workspace (its dock's Reading or Walkthrough) is told
// about its tools on that workspace, after what it is told about its own.
export const WORKSPACE_TOOLS_SUFFIX = `# The investigation beside this conversation

The reader has this conversation open beside an investigation of traces; what is on it comes with each question, in <dashboard_context>. You can change it when the reader asks you to, or when it plainly helps them see what you found:
- workspace_state reads it: its traces, and \`things\`, what is about them (findings, notes, the chat's claims, sub-agents), each with its id and whether its card is open.
- workspace_lay_on lays sessions on it, and workspace_take_off takes them off (never the trace it is anchored on).
- workspace_open opens a thing's card under its trace, and closes it with open: false; nothing shows under a trace until it is opened.
- workspace_point pins a note on a turn of any trace on it.
Say in your answer what you changed on the workspace.`;
